import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { ModalButtons } from "../../../shared/component/modal-container/modal-container.component";
import { SharedComponentsModule } from "../../../shared/module/shared-components.module";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { KpiRecipient, KpiRecipientFormGroup } from "../../dto/KpiMember";
import { POSITION_ROLES } from "../../../organization/components/shared/team-modal-crud/team-modal-crud.component";
import { EmployeeService } from "../../../organization/service/employee.service";
import { KpiRequestCompanyMini } from "../kpi-modal-bulk-kpi-request/kpi-modal-bulk-kpi-request.component";
import { firstValueFrom, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: 'app-kpi-modal-company-member-crud',
  standalone: true,
  imports: [
    SharedComponentsModule,
  ],
  templateUrl: './kpi-modal-company-member-crud.component.html',
  styleUrl: './kpi-modal-company-member-crud.component.scss',
})
export class KpiModalCompanyMemberCrudComponent implements OnChanges, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();

  @Input({ required: true }) member: KpiRecipient;
  @Input({ required: true }) isDeleteEnabled: boolean;
  @Input({ required: true }) company: KpiRequestCompanyMini;
  @Output() closeEmitter = new EventEmitter<CrudKpiMemberResponse>();
  modalButtons: ModalButtons = {
    close: true,
    dismiss: 'Cancel',
    apply: 'Apply',
  };

  positions = POSITION_ROLES;

  showDeleteEmployeeModal = false;
  teamMemberForm: KpiRecipientFormGroup;

  constructor(private employeeService: EmployeeService) {
  }

  ngOnChanges(): void {
    this.initVariables();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async confirm(): Promise<void> {
    if (this.member) {
      await this.updateMember();
      this.close(true, 'update');
    } else {
      await this.createMember();
      this.close(true, 'create');
    }
  }

  async createMember(): Promise<void> {
    const createdMember =
      await firstValueFrom(this.employeeService.create(this.teamMemberForm.getRawValue(), true).pipe(takeUntil(this._destroyStream)));
    this.teamMemberForm.controls.uuid.patchValue(createdMember.uuid);
  }

  async updateMember(): Promise<void> {
    await this.employeeService.update(this.teamMemberForm.getRawValue(), true);
  }

  async deleteTeamMember(isSuccessful: boolean): Promise<void> {
    this.showDeleteEmployeeModal = false;
    if (isSuccessful) {
      await this.employeeService.delete(this.member.uuid);
      this.close(true, 'delete');
    } else {
      this.close(false, 'delete');
    }
  }

  close(isSuccessful: boolean, status?: CrudKpiMemberStatus): void {
    if (!isSuccessful) {
      this.closeEmitter.emit(null);
      return;
    }
    const crudKpiMemberResponse: CrudKpiMemberResponse = {
      status: status,
      member: this.teamMemberForm.getRawValue(),
    };
    this.closeEmitter.emit(crudKpiMemberResponse);
  }

  deleteButtonClicked(): void {
    this.showDeleteEmployeeModal = true;
  }

  private initVariables(): void {
    this.initForm();
    if (this.member) {
      this.modalButtons.lowerLeft = 'Delete';
      this.modalButtons.lowerLeftClass = 'light-red';
      this.modalButtons.lowerLeftIcon = 'delete.svg';
      if (this.isDeleteEnabled) {
        this.showDeleteEmployeeModal = true;
      }
    }
  }

  private initForm(): void {
    this.teamMemberForm = new FormGroup({
      uuid: new FormControl<string>(this.member ? this.member.uuid : null),
      name: new FormControl<string>(this.member ? this.member.name : null),
      family: new FormControl<string>(this.member ? this.member.family : null),
      companyUUID: new FormControl<string>(this.company.uuid),
      companyName: new FormControl<string>(this.member ? this.member.companyName : this.company.name),
      position: new FormControl<string>(this.member ? this.member.position : null),
      mail: new FormControl<string>(this.member ? this.member.mail : null, { validators: Validators.email }),
      phone: new FormControl<string>(this.member ? this.member.phone : null),
    });
  }
}

export interface CrudKpiMemberResponse {
  member: KpiRecipient,
  status: CrudKpiMemberStatus
}

export type CrudKpiMemberStatus = 'create' | 'update' | 'delete'
